import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { CircleAlert, CircleCheck, Info, TriangleAlert, X } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import { randomId } from '@/shared/lib/random-id';

/**
 * A small toast stack.
 *
 * Deliberately not a dependency: the API already returns a localised message
 * for every failure, so all this needs to do is display a string and get out
 * of the way. Auto-dismiss timings differ by severity — an error the user may
 * need to read stays three times as long as a confirmation.
 */

export type ToastVariant = 'success' | 'error' | 'warning' | 'info';

export interface ToastOptions {
  title: string;
  description?: string;
  variant?: ToastVariant;
  /** Milliseconds before auto-dismiss; `0` keeps it until dismissed. */
  duration?: number;
}

interface ToastRecord extends Required<Omit<ToastOptions, 'description'>> {
  id: string;
  description?: string;
}

interface ToastApi {
  success: (title: string, description?: string) => string;
  error: (title: string, description?: string) => string;
  warning: (title: string, description?: string) => string;
  info: (title: string, description?: string) => string;
  push: (options: ToastOptions) => string;
  dismiss: (id: string) => void;
}

const DEFAULT_DURATION: Record<ToastVariant, number> = {
  success: 3500,
  info: 4000,
  warning: 5500,
  error: 7000,
};

const VARIANT_STYLES: Record<ToastVariant, { container: string; icon: string }> = {
  success: { container: 'border-success/30', icon: 'text-success' },
  error: { container: 'border-destructive/40', icon: 'text-destructive' },
  warning: { container: 'border-warning/40', icon: 'text-warning' },
  info: { container: 'border-info/40', icon: 'text-info' },
};

const ToastContext = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const [toasts, setToasts] = useState<ToastRecord[]>([]);
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: string) => {
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const push = useCallback(
    ({ title, description, variant = 'info', duration }: ToastOptions): string => {
      const id = randomId();
      const record: ToastRecord = {
        id,
        title,
        description,
        variant,
        duration: duration ?? DEFAULT_DURATION[variant],
      };

      // Cap the stack so a burst of failures cannot cover the screen.
      setToasts((current) => [...current.slice(-4), record]);

      if (record.duration > 0) {
        timers.current.set(
          id,
          setTimeout(() => dismiss(id), record.duration),
        );
      }

      return id;
    },
    [dismiss],
  );

  const api = useMemo<ToastApi>(
    () => ({
      push,
      dismiss,
      success: (title, description) => push({ title, description, variant: 'success' }),
      error: (title, description) => push({ title, description, variant: 'error' }),
      warning: (title, description) => push({ title, description, variant: 'warning' }),
      info: (title, description) => push({ title, description, variant: 'info' }),
    }),
    [push, dismiss],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        className={cn(
          'pointer-events-none fixed inset-x-0 bottom-0 z-[100] flex flex-col items-center gap-2 p-4',
          'sm:inset-x-auto sm:right-0 sm:items-end',
        )}
        style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
      >
        {toasts.map((toast) => {
          const styles = VARIANT_STYLES[toast.variant];
          const Icon =
            toast.variant === 'success'
              ? CircleCheck
              : toast.variant === 'error'
                ? CircleAlert
                : toast.variant === 'warning'
                  ? TriangleAlert
                  : Info;

          return (
            <div
              key={toast.id}
              role={toast.variant === 'error' ? 'alert' : 'status'}
              aria-live={toast.variant === 'error' ? 'assertive' : 'polite'}
              className={cn(
                'bg-popover text-popover-foreground pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-lg border p-3 shadow-lg',
                'animate-toast-in',
                styles.container,
              )}
            >
              <Icon className={cn('mt-0.5 size-4 shrink-0', styles.icon)} aria-hidden />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium">{toast.title}</p>
                {toast.description ? (
                  <p className="text-muted-foreground mt-0.5 text-xs break-words">
                    {toast.description}
                  </p>
                ) : null}
              </div>
              <button
                type="button"
                onClick={() => dismiss(toast.id)}
                className="text-muted-foreground hover:text-foreground -mt-0.5 -mr-1 rounded-md p-1 transition-colors"
                aria-label={t('common.close')}
              >
                <X className="size-3.5" />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used inside <ToastProvider>');
  }
  return context;
}
